import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Store, User, Mail, Phone, Key, Calendar, DollarSign, CreditCard } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { shopService } from '../../services/shopService';
import { customToast } from '../../utils/customToast';
import { format, addMonths } from 'date-fns';

interface CreateShopModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const CreateShopModal: React.FC<CreateShopModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [formData, setFormData] = useState({
    shop_name: '',
    owner_name: '',
    owner_email: '',
    owner_phone: '',
    business_type: '',
    owner_username: '',
    owner_password: '',
    subscription_status: 'trial' as 'trial' | 'active',
    payment_status: 'pending' as 'pending' | 'paid' | 'overdue',
    subscription_starts_at: '',
    subscription_ends_at: '',
    trial_ends_at: '',
    // Payment fields
    payment_amount: '',
    payment_date: format(new Date(), 'yyyy-MM-dd'),
    payment_method: 'cash',
    payment_reference: '',
    subscription_months: '12',
    payment_notes: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Calculate subscription period when payment is included
  const subscriptionPeriod = useMemo(() => {
    // Check if payment fields have values
    const hasPaymentData = formData.payment_amount && formData.payment_date && formData.subscription_months;
    
    if (!hasPaymentData) {
      return null;
    }
    
    const paymentDate = new Date(formData.payment_date);
    const months = parseInt(formData.subscription_months);
    const endDate = addMonths(paymentDate, months);
    
    // End date is the same date next year (e.g., Oct 10, 2025 + 12 months = Oct 10, 2026)
    // Next payment is due the day after (Oct 11, 2026)
    const nextPaymentDate = new Date(endDate);
    nextPaymentDate.setDate(nextPaymentDate.getDate() + 1);
    
    return {
      startDate: paymentDate,
      endDate: endDate,
      nextPaymentDate: nextPaymentDate,
    };
  }, [formData.payment_amount, formData.payment_date, formData.subscription_months]);

  if (!isOpen && !isClosing) return null;

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.shop_name.trim()) newErrors.shop_name = 'Shop name is required';
    if (!formData.business_type.trim()) newErrors.business_type = 'Business type is required';
    if (!formData.owner_name.trim()) newErrors.owner_name = 'Owner name is required';
    if (!formData.owner_email.trim()) {
      newErrors.owner_email = 'Owner email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.owner_email)) {
      newErrors.owner_email = 'Invalid email format';
    }
    if (!formData.owner_username.trim()) {
      newErrors.owner_username = 'Username is required';
    } else if (formData.owner_username.length < 4) {
      newErrors.owner_username = 'Username must be at least 4 characters';
    }
    if (!formData.owner_password.trim()) {
      newErrors.owner_password = 'Password is required';
    } else if (formData.owner_password.length < 6) {
      newErrors.owner_password = 'Password must be at least 6 characters';
    }

    // Validate payment fields if any payment data is entered
    const hasAnyPaymentData = formData.payment_amount || formData.payment_date !== format(new Date(), 'yyyy-MM-dd') || formData.payment_method !== 'cash';
    
    if (hasAnyPaymentData) {
      if (!formData.payment_amount || parseFloat(formData.payment_amount) <= 0) {
        newErrors.payment_amount = 'Payment amount must be greater than 0';
      }
      if (!formData.payment_date) {
        newErrors.payment_date = 'Payment date is required';
      }
      if (!formData.payment_method) {
        newErrors.payment_method = 'Payment method is required';
      }
      if (!formData.subscription_months || parseInt(formData.subscription_months) < 1 || parseInt(formData.subscription_months) > 24) {
        newErrors.subscription_months = 'Subscription months must be between 1 and 24';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      customToast.error('Please fix the validation errors');
      return;
    }

    try {
      setLoading(true);
      
      // Check if payment data is provided
      const hasPaymentData = formData.payment_amount && parseFloat(formData.payment_amount) > 0;
      
      // Prepare shop data
      const shopData: any = {
        shop_name: formData.shop_name,
        owner_name: formData.owner_name,
        owner_email: formData.owner_email,
        owner_phone: formData.owner_phone,
        business_type: formData.business_type,
        owner_username: formData.owner_username,
        owner_password: formData.owner_password,
        subscription_status: hasPaymentData ? 'active' : formData.subscription_status,
        payment_status: hasPaymentData ? 'paid' : formData.payment_status,
        subscription_starts_at: formData.subscription_starts_at,
        subscription_ends_at: formData.subscription_ends_at,
        trial_ends_at: formData.trial_ends_at,
      };

      // If payment is included, add payment data and calculate subscription dates
      if (hasPaymentData && subscriptionPeriod) {
        shopData.payment = {
          amount: parseFloat(formData.payment_amount),
          payment_date: formData.payment_date,
          payment_method: formData.payment_method,
          reference: formData.payment_reference || null,
          subscription_months: parseInt(formData.subscription_months),
          notes: formData.payment_notes || null,
        };
        
        // Set subscription dates based on payment
        shopData.subscription_starts_at = format(subscriptionPeriod.startDate, 'yyyy-MM-dd');
        shopData.subscription_ends_at = format(subscriptionPeriod.endDate, 'yyyy-MM-dd');
        shopData.subscription_status = 'active';
        shopData.payment_status = 'paid';
      }
      
      await shopService.createShop(shopData);
      customToast.success(hasPaymentData ? 'Shop created with payment recorded successfully' : 'Shop created successfully');
      onSuccess();
      handleClose();
    } catch (error: any) {
      console.error('Error creating shop:', error);
      customToast.error(error.response?.data?.message || 'Failed to create shop');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      shop_name: '',
      owner_name: '',
      owner_email: '',
      owner_phone: '',
      business_type: '',
      owner_username: '',
      owner_password: '',
      subscription_status: 'trial',
      payment_status: 'pending',
      subscription_starts_at: '',
      subscription_ends_at: '',
      trial_ends_at: '',
      payment_amount: '',
      payment_date: format(new Date(), 'yyyy-MM-dd'),
      payment_method: 'cash',
      payment_reference: '',
      subscription_months: '12',
      payment_notes: '',
    });
    setErrors({});
    if (!loading) {
      setIsClosing(true);
      setTimeout(() => {
        setIsClosing(false);
        onClose();
      }, 150);
    }
  };

  const modalContent = (
    <div 
      className={`fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-[9999] ${
        isClosing ? 'animate-fadeOut' : 'animate-fadeIn'
      }`}
      onClick={() => !loading && handleClose()}
    >
      <div 
        className={`w-full max-w-2xl max-h-[90vh] flex flex-col rounded-xl shadow-2xl ${
          isClosing ? 'animate-scaleOut' : 'animate-scaleIn'
        } ${theme === 'dark' ? 'bg-slate-800 border border-slate-700' : 'bg-white border border-gray-200'}`}
        onClick={(e) => e.stopPropagation()}
      >
          {/* Header */}
          <div className={`px-4 sm:px-5 md:px-6 py-3 sm:py-4 border-b rounded-t-xl flex-shrink-0 ${theme === 'dark' ? 'border-gray-700 bg-slate-800/95' : 'border-gray-200 bg-gray-50/95'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
                <div className={`p-2 rounded-xl flex-shrink-0 ${theme === 'dark' ? 'bg-blue-500/10' : 'bg-blue-50'}`}>
                  <Store className={`w-5 h-5 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`} />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className={`text-base sm:text-lg font-semibold truncate ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    Create New Shop
                  </h3>
                  <p className={`text-xs sm:text-sm truncate ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    Enter shop and owner details
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                type="button"
                className={`p-2 rounded-xl transition-all duration-200 touch-manipulation flex-shrink-0 ${
                  theme === 'dark' 
                    ? 'text-gray-400 hover:text-gray-200 hover:bg-slate-700' 
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} id="create-shop-form" className="flex-1 overflow-y-auto">
            <div className="px-4 sm:px-5 md:px-6 py-4 sm:py-5">
              <div className="space-y-4 sm:space-y-5 md:space-y-6">
              {/* Shop Information */}
              <div>
                <h4 className={`text-xs sm:text-sm font-medium mb-2 sm:mb-3 md:mb-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  Shop Information
                </h4>
                <div className="space-y-2.5 sm:space-y-3 md:space-y-4">
                  <div>
                    <label className={`block text-xs sm:text-sm font-medium mb-1 sm:mb-1.5 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                      Shop Name *
                    </label>
                    <div className="relative">
                      <Store className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 sm:h-4 sm:w-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                      <input
                        type="text"
                        value={formData.shop_name}
                        onChange={(e) => setFormData({ ...formData, shop_name: e.target.value })}
                        className={`w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-2.5 text-sm rounded-lg sm:rounded-xl border transition-colors touch-manipulation ${
                          errors.shop_name
                            ? 'border-red-500 focus:ring-red-500'
                            : theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500'
                            : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                        } focus:outline-none focus:ring-2 focus:ring-opacity-20`}
                        placeholder="Enter shop name"
                      />
                    </div>
                    {errors.shop_name && <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.shop_name}</p>}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 sm:gap-3 md:gap-4">
                    <div>
                      <label className={`block text-xs sm:text-sm font-medium mb-1 sm:mb-1.5 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                        Business Type *
                      </label>
                      <select
                        value={formData.business_type}
                        onChange={(e) => setFormData({ ...formData, business_type: e.target.value })}
                        className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm rounded-lg sm:rounded-xl border transition-colors touch-manipulation ${
                          errors.business_type ? 'border-red-500' : theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                        } focus:outline-none focus:ring-2 focus:ring-opacity-20`}
                      >
                        <option value="">Select business type</option>
                        <option value="retail">Retail</option>
                        <option value="wholesale">Wholesale</option>
                        <option value="restaurant">Restaurant</option>
                        <option value="pharmacy">Pharmacy</option>
                        <option value="grocery">Grocery</option>
                        <option value="electronics">Electronics</option>
                        <option value="clothing">Clothing</option>
                        <option value="other">Other</option>
                      </select>
                      {errors.business_type && <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.business_type}</p>}
                    </div>
                  </div>
                </div>
              </div>

              {/* Owner Information */}
              <div>
                <h4 className={`text-xs sm:text-sm font-medium mb-2 sm:mb-3 md:mb-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  Owner Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 sm:gap-3 md:gap-4">
                  <div>
                    <label className={`block text-xs sm:text-sm font-medium mb-1 sm:mb-1.5 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                      Owner Name *
                    </label>
                    <div className="relative">
                      <User className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 sm:h-4 sm:w-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                      <input
                        type="text"
                        value={formData.owner_name}
                        onChange={(e) => setFormData({ ...formData, owner_name: e.target.value })}
                        className={`w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-2.5 text-sm rounded-lg sm:rounded-xl border transition-colors touch-manipulation ${
                          errors.owner_name ? 'border-red-500' : theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                        } focus:outline-none focus:ring-2 focus:ring-opacity-20`}
                        placeholder="Enter owner name"
                      />
                    </div>
                    {errors.owner_name && <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.owner_name}</p>}
                  </div>

                  <div>
                    <label className={`block text-xs sm:text-sm font-medium mb-1 sm:mb-1.5 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                      Owner Email *
                    </label>
                    <div className="relative">
                      <Mail className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 sm:h-4 sm:w-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                      <input
                        type="email"
                        value={formData.owner_email}
                        onChange={(e) => setFormData({ ...formData, owner_email: e.target.value })}
                        className={`w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-2.5 text-sm rounded-lg sm:rounded-xl border transition-colors touch-manipulation ${
                          errors.owner_email ? 'border-red-500' : theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                        } focus:outline-none focus:ring-2 focus:ring-opacity-20`}
                        placeholder="owner@example.com"
                      />
                    </div>
                    {errors.owner_email && <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.owner_email}</p>}
                  </div>

                  <div>
                    <label className={`block text-xs sm:text-sm font-medium mb-1 sm:mb-1.5 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                      Phone Number
                    </label>
                    <div className="relative">
                      <Phone className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 sm:h-4 sm:w-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                      <input
                        type="tel"
                        value={formData.owner_phone}
                        onChange={(e) => setFormData({ ...formData, owner_phone: e.target.value })}
                        className={`w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-2.5 text-sm rounded-lg sm:rounded-xl border transition-colors touch-manipulation ${
                          theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                        } focus:outline-none focus:ring-2 focus:ring-opacity-20`}
                        placeholder="+1 (555) 000-0000"
                      />
                    </div>
                  </div>

                  <div>
                    <label className={`block text-xs sm:text-sm font-medium mb-1 sm:mb-1.5 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                      Username *
                    </label>
                    <div className="relative">
                      <User className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 sm:h-4 sm:w-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                      <input
                        type="text"
                        value={formData.owner_username}
                        onChange={(e) => setFormData({ ...formData, owner_username: e.target.value })}
                        className={`w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-2.5 text-sm rounded-lg sm:rounded-xl border transition-colors touch-manipulation ${
                          errors.owner_username ? 'border-red-500' : theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                        } focus:outline-none focus:ring-2 focus:ring-opacity-20`}
                        placeholder="username"
                      />
                    </div>
                    {errors.owner_username && <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.owner_username}</p>}
                  </div>

                  <div className="md:col-span-2">
                    <label className={`block text-xs sm:text-sm font-medium mb-1 sm:mb-1.5 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                      Password *
                    </label>
                    <div className="relative">
                      <Key className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 sm:h-4 sm:w-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                      <input
                        type="password"
                        value={formData.owner_password}
                        onChange={(e) => setFormData({ ...formData, owner_password: e.target.value })}
                        className={`w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-2.5 text-sm rounded-lg sm:rounded-xl border transition-colors touch-manipulation ${
                          errors.owner_password ? 'border-red-500' : theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                        } focus:outline-none focus:ring-2 focus:ring-opacity-20`}
                        placeholder="Enter password (min 6 characters)"
                      />
                    </div>
                    {errors.owner_password && <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.owner_password}</p>}
                  </div>
                </div>
              </div>

              {/* Subscription Settings */}
              <div>
                <h4 className={`text-xs sm:text-sm font-medium mb-2 sm:mb-3 md:mb-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  Subscription Settings
                </h4>
                <div className="space-y-2.5 sm:space-y-3 md:space-y-4">
                  <div>
                    <label className={`block text-xs sm:text-sm font-medium mb-1 sm:mb-1.5 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                      Initial Status *
                    </label>
                    <select
                      value={formData.subscription_status}
                      onChange={(e) => {
                        const newStatus = e.target.value as 'trial' | 'active';
                        const today = format(new Date(), 'yyyy-MM-dd');
                        const nextYear = format(addMonths(new Date(), 12), 'yyyy-MM-dd');
                        
                        setFormData({ 
                          ...formData, 
                          subscription_status: newStatus,
                          // Auto-fill dates when Active is selected
                          subscription_starts_at: newStatus === 'active' ? today : '',
                          subscription_ends_at: newStatus === 'active' ? nextYear : '',
                        });
                      }}
                      className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm rounded-lg sm:rounded-xl border transition-colors touch-manipulation ${
                        theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                      } focus:outline-none focus:ring-2 focus:ring-opacity-20`}
                    >
                      <option value="trial">Trial</option>
                      <option value="active">Active</option>
                    </select>
                  </div>

                  {/* Trial End Date - Only show when Trial */}
                  {formData.subscription_status === 'trial' && (
                    <div>
                      <label className={`block text-xs sm:text-sm font-medium mb-1 sm:mb-1.5 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                        Trial End Date *
                      </label>
                      <div className="relative">
                        <Calendar className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 sm:h-4 sm:w-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                        <input
                          type="date"
                          value={formData.subscription_ends_at}
                          onChange={(e) => setFormData({ ...formData, subscription_ends_at: e.target.value })}
                          className={`w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-2.5 text-sm rounded-lg sm:rounded-xl border transition-colors touch-manipulation ${
                            theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                          } focus:outline-none focus:ring-2 focus:ring-opacity-20`}
                        />
                      </div>
                    </div>
                  )}

                  {/* Subscription Date Fields - Only show when Active */}
                  {formData.subscription_status === 'active' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 sm:gap-3 md:gap-4">
                      <div>
                        <label className={`block text-xs sm:text-sm font-medium mb-1 sm:mb-1.5 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                          Subscription Start Date *
                        </label>
                        <div className="relative">
                          <Calendar className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 sm:h-4 sm:w-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                          <input
                            type="date"
                            value={formData.subscription_starts_at}
                            onChange={(e) => setFormData({ ...formData, subscription_starts_at: e.target.value })}
                            className={`w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-2.5 text-sm rounded-lg sm:rounded-xl border transition-colors touch-manipulation ${
                              theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                            } focus:outline-none focus:ring-2 focus:ring-opacity-20`}
                          />
                        </div>
                      </div>

                      <div>
                        <label className={`block text-xs sm:text-sm font-medium mb-1 sm:mb-1.5 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                          Subscription End Date *
                        </label>
                        <div className="relative">
                          <Calendar className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 sm:h-4 sm:w-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                          <input
                            type="date"
                            value={formData.subscription_ends_at}
                            onChange={(e) => setFormData({ ...formData, subscription_ends_at: e.target.value })}
                            className={`w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-2.5 text-sm rounded-lg sm:rounded-xl border transition-colors touch-manipulation ${
                              theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                            } focus:outline-none focus:ring-2 focus:ring-opacity-20`}
                          />
                        </div>
                      </div>

                      <div className="md:col-span-2">
                        <label className={`block text-xs sm:text-sm font-medium mb-1 sm:mb-1.5 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                          Payment Status
                        </label>
                        <select
                          value={formData.payment_status}
                          onChange={(e) => setFormData({ ...formData, payment_status: e.target.value as any })}
                          className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm rounded-lg sm:rounded-xl border transition-colors touch-manipulation ${
                            theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                          } focus:outline-none focus:ring-2 focus:ring-opacity-20`}
                        >
                          <option value="pending">Pending</option>
                          <option value="paid">Paid</option>
                          <option value="overdue">Overdue</option>
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Payment Information (Optional) - Only show when Active */}
              {formData.subscription_status === 'active' && (
                <div>
                  <h4 className={`text-xs sm:text-sm font-medium mb-2 sm:mb-3 md:mb-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Record Initial Payment (Optional)
                  </h4>
                  <div className="space-y-2.5 sm:space-y-3 md:space-y-4">
                    <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                      Record the initial payment and subscription will be automatically set to Active
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 sm:gap-3 md:gap-4">
                      <div>
                        <label className={`block text-xs sm:text-sm font-medium mb-1 sm:mb-1.5 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                          Payment Amount *
                        </label>
                        <div className="relative">
                          <DollarSign className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 sm:h-4 sm:w-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.payment_amount}
                            onChange={(e) => setFormData({ ...formData, payment_amount: e.target.value })}
                            className={`w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-2.5 text-sm rounded-lg sm:rounded-xl border transition-colors touch-manipulation ${
                              errors.payment_amount ? 'border-red-500' : theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                            } focus:outline-none focus:ring-2 focus:ring-opacity-20`}
                            placeholder="0.00"
                          />
                        </div>
                        {errors.payment_amount && <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.payment_amount}</p>}
                      </div>

                      <div>
                        <label className={`block text-xs sm:text-sm font-medium mb-1 sm:mb-1.5 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                          Payment Date *
                        </label>
                        <div className="relative">
                          <Calendar className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 sm:h-4 sm:w-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                          <input
                            type="date"
                            value={formData.payment_date}
                            onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })}
                            className={`w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-2.5 text-sm rounded-lg sm:rounded-xl border transition-colors touch-manipulation ${
                              errors.payment_date ? 'border-red-500' : theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                            } focus:outline-none focus:ring-2 focus:ring-opacity-20`}
                          />
                        </div>
                        {errors.payment_date && <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.payment_date}</p>}
                      </div>

                      <div>
                        <label className={`block text-xs sm:text-sm font-medium mb-1 sm:mb-1.5 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                          Payment Method *
                        </label>
                        <div className="relative">
                          <CreditCard className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 sm:h-4 sm:w-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                          <select
                            value={formData.payment_method}
                            onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                            className={`w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-2.5 text-sm rounded-lg sm:rounded-xl border transition-colors touch-manipulation ${
                              errors.payment_method ? 'border-red-500' : theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                            } focus:outline-none focus:ring-2 focus:ring-opacity-20`}
                          >
                            <option value="cash">Cash</option>
                            <option value="bank_transfer">Bank Transfer</option>
                            <option value="credit_card">Credit Card</option>
                            <option value="mobile_payment">Mobile Payment</option>
                            <option value="check">Check</option>
                            <option value="other">Other</option>
                          </select>
                        </div>
                        {errors.payment_method && <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.payment_method}</p>}
                      </div>

                      <div>
                        <label className={`block text-xs sm:text-sm font-medium mb-1 sm:mb-1.5 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                          Subscription Months *
                        </label>
                        <input
                          type="number"
                          min="1"
                          max="24"
                          value={formData.subscription_months}
                          onChange={(e) => setFormData({ ...formData, subscription_months: e.target.value })}
                          className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm rounded-lg sm:rounded-xl border transition-colors touch-manipulation ${
                            errors.subscription_months ? 'border-red-500' : theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                          } focus:outline-none focus:ring-2 focus:ring-opacity-20`}
                          placeholder="12"
                        />
                        {errors.subscription_months && <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.subscription_months}</p>}
                      </div>

                      <div className="md:col-span-2">
                        <label className={`block text-xs sm:text-sm font-medium mb-1 sm:mb-1.5 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                          Payment Reference
                        </label>
                        <input
                          type="text"
                          value={formData.payment_reference}
                          onChange={(e) => setFormData({ ...formData, payment_reference: e.target.value })}
                          className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm rounded-lg sm:rounded-xl border transition-colors touch-manipulation ${
                            theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                          } focus:outline-none focus:ring-2 focus:ring-opacity-20`}
                          placeholder="Transaction ID, receipt number, etc."
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className={`block text-xs sm:text-sm font-medium mb-1 sm:mb-1.5 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                          Notes
                        </label>
                        <textarea
                          rows={2}
                          value={formData.payment_notes}
                          onChange={(e) => setFormData({ ...formData, payment_notes: e.target.value })}
                          className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm rounded-lg sm:rounded-xl border transition-colors touch-manipulation resize-none ${
                            theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                          } focus:outline-none focus:ring-2 focus:ring-opacity-20`}
                          placeholder="Additional payment notes..."
                        />
                      </div>
                    </div>

                    {/* Subscription Period Preview */}
                    {subscriptionPeriod && (
                      <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-blue-900/20 border border-blue-800/30' : 'bg-blue-50 border border-blue-200'}`}>
                        <p className={`text-xs font-medium mb-2 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-700'}`}>
                          Subscription Period Preview:
                        </p>
                        <div className="space-y-1 text-xs">
                          <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
                            <span className="font-medium">Start:</span> {format(subscriptionPeriod.startDate, 'MMM dd, yyyy')}
                          </p>
                          <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
                            <span className="font-medium">End:</span> {format(subscriptionPeriod.endDate, 'MMM dd, yyyy')}
                          </p>
                          <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
                            <span className="font-medium">Next Payment Due:</span> {format(subscriptionPeriod.nextPaymentDate, 'MMM dd, yyyy')}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              </div>
            </div>
          </form>

            {/* Footer */}
            <div className={`px-4 sm:px-5 md:px-6 py-3 sm:py-4 border-t rounded-b-xl flex-shrink-0 ${
              theme === 'dark' ? 'border-gray-700 bg-slate-800/95' : 'border-gray-200 bg-gray-50/95'
            }`}>
            <div className="flex flex-col-reverse sm:flex-row gap-3 justify-end">
              <button
                type="button"
                onClick={handleClose}
                disabled={loading}
                className={`px-4 sm:px-5 md:px-6 py-2.5 sm:py-3 text-sm sm:text-base border rounded-xl font-medium transition-all duration-200 shadow-lg hover:shadow-xl touch-manipulation ${
                  theme === 'dark' 
                    ? 'border-gray-600 text-gray-300 hover:bg-slate-700 hover:border-gray-500' 
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
                } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                Cancel
              </button>
              <button
                type="submit"
                form="create-shop-form"
                disabled={loading}
                className={`px-4 sm:px-5 md:px-6 py-2.5 sm:py-3 text-sm sm:text-base bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-medium transition-all duration-200 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl touch-manipulation ${
                  loading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {loading && (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                )}
                <span>{loading ? 'Creating...' : 'Create Shop'}</span>
              </button>
            </div>
          </div>
        </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default CreateShopModal;
